import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Zaptec vs Easee — bespoke rich comparison page.
 * Geo-aware: reads page.geography to localise headline/intro.
 * Registered for: zaptec-vs-easee + all regional variants.
 *
 * Sources: Zaptec product specs (zaptec.com), Easee product specs (easee.com),
 * Type 2 / IEC 62196 standard. No competitor electricity-seller pricing used.
 */
export default function ZaptecVsEasee({ page }) {
  const geo = page?.geography;
  const place = geo?.name ? ` i ${geo.name}` : '';
  const zone = geo?.zone;
  const zoneLabel = zone === 'DK1' ? 'DK1 (Vestdanmark)' : zone === 'DK2' ? 'DK2 (Østdanmark)' : null;

  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Zaptec eller Easee – hvad er bedst?',
      answer: 'Begge er kvalitets-ladebokse med Type 2-stik og op til 22 kW. Zaptec (norsk) er kendt for robust load balancing og er Nordens mest solgte. Easee (norsk) er kendt for sit kompakte design og var i en periode underlagt et midlertidigt salgsstop i Sverige i 2023 pga. en sikkerhedsvurdering, hvilket senere blev afklaret. Hos enkel energi forhandler vi Zaptec Go, Zaptec Go 2 og NexBlue Edge 2 – men vores Saving Mode og laderabat virker også med en Easee, du allerede har.',
    },
    {
      question: 'Kan jeg bruge min Easee med enkel energi?',
      answer: 'Ja. Selvom vi ikke sælger Easee, er enkel Saving Mode og laderabat (10 øre/kWh) kompatible med Easee Home, Charge, Charge Lite og Peak. Du tilslutter blot din eksisterende Easee til enkel-appen og får alle fordelene – uden at købe ny hardware.',
    },
    {
      question: 'Er der forskel på ladehastigheden mellem Zaptec og Easee?',
      answer: 'Nej. Begge mærker leverer op til 22 kW på en 3-faset installation. Den reelle hastighed afhænger af, hvad din bil og din elinstallation kan trække – ikke af mærket. Forskellene ligger i forbindelse, måler, design og fremtidssikring.',
    },
    {
      question: 'Hvilken ladeboks anbefaler enkel?',
      answer: `For de fleste er NexBlue Edge 2 (${ENKEL_PRODUCTS.chargers.nexblue_edge_2.price.toLocaleString('da-DK')} kr.) det bedste valg til prisen, mens Zaptec Go (${ENKEL_PRODUCTS.chargers.zaptec_go.price.toLocaleString('da-DK')} kr.) er det velkendte, gennemtestede valg. Begge inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md.`,
    },
    {
      question: 'Har Zaptec og Easee en godkendt måler til elafgift-refusion?',
      answer: 'Ja. Zaptec Go 2 har en MID-certificeret måler, og Zaptec Go samt Easee-modeller har målefunktioner, der kan dokumentere dit elbil-forbrug. Det er forudsætningen for at få refunderet elafgiften på din hjemmeopladning – noget enkel energi håndterer automatisk via serviceaftalen.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'tabel', label: 'Side-om-side' },
    { id: 'zaptec', label: 'Om Zaptec' },
    { id: 'easee', label: 'Om Easee' },
    { id: 'anbefaling', label: 'Anbefaling' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: `Zaptec vs Easee${place}`, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={`Zaptec vs Easee${place}`} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Sammenligning · Ladebokse${zoneLabel ? ' · ' + zone : ''} · 2026`}
        h1={`Zaptec vs Easee${place}: Hvilken ladeboks skal du vælge?`}
        intro={`To af Nordens mest populære ladeboks-mærker stillet op mod hinanden${place}. Vi sammenligner pris, måler, forbindelse og fremtidssikring – og forklarer, hvordan du får Saving Mode og laderabat, uanset hvilken du vælger.`}
        badges={['Begge op til 22 kW', 'Type 2-stik', 'Saving Mode virker med begge']}
        updated={updated}
        image="https://images.unsplash.com/photo-1647500666543-2f5f1e7e0b6e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '22 kW', label: 'begge mærker (3-faset)' },
          { value: 'Type 2', label: 'standard for alle elbiler' },
          { value: '10 øre/kWh', label: 'laderabat med begge' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Zaptec eller Easee – kort svar">
        <AnswerBox question="Hvilken er bedst – Zaptec eller Easee?">
          Begge er solide Type 2-ladebokse med op til <strong>22 kW</strong>. <strong>Zaptec</strong> er Nordens mest solgte og kendt for robust
          load balancing; <strong>Easee</strong> for sit kompakte design. Forskellen i ladehastighed er ingen – begge leverer 22 kW.
          Vælger du gennem enkel energi, sælger vi Zaptec Go, Zaptec Go 2 og NexBlue Edge 2 – men <strong>Saving Mode og laderabat virker også
          med en Easee, du allerede ejer</strong>.
        </AnswerBox>
        {zoneLabel && (
          <p>
            {geo.name} ligger i prisområde <strong>{zoneLabel}</strong>. Spotprisen følger Nord Pool time for time, og uanset om du vælger
            Zaptec eller Easee, kan enkel Saving Mode automatisk flytte din opladning til de billigste timer i {zone}.
          </p>
        )}
      </ProseSection>

      <ProseSection id="tabel" heading="Zaptec vs Easee – side om side" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Sammenligning af Zaptec og Easee ladebokse"
            columns={[{ name: 'Zaptec Go / Go 2' }, { name: 'Easee Home / Charge' }]}
            rows={[
              { label: 'Producent', values: ['Zaptec (Norge)', 'Easee (Norge)'] },
              { label: 'Max effekt', values: ['22 kW', '22 kW'] },
              { label: 'Stiktype', values: ['Type 2', 'Type 2'] },
              { label: 'Forbindelse', values: ['Wi-Fi + 4G', 'Wi-Fi + 4G (Charge)'] },
              { label: 'Load balancing', values: [true, true] },
              { label: 'MID-måler', values: ['Ja (Go 2)', 'Afhænger af model'] },
              { label: 'V2G-ready', values: ['Ja (Go 2)', false] },
              { label: 'Sælges hos enkel', values: [true, false] },
              { label: 'Saving Mode-kompatibel', values: [true, true] },
              { label: 'Laderabat 10 øre/kWh', values: [true, true] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">Kilder: Zaptec og Easee officielle produktspecifikationer. Type 2 / IEC 62196-standard.</p>
      </ProseSection>

      <div className="container mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div id="zaptec">
            <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go} note="Nordens mest solgte" />
            <div className="mt-4">
              <ProsCons
                pros={['Robust, gennemtestet load balancing', 'Nordens mest solgte ladeboks', 'Go 2 har MID-måler + V2G-ready', 'Sælges og supporteres af enkel']}
                cons={['Go har ingen indbygget skærm', 'Go 2 er den dyrere model']}
              />
            </div>
          </div>
          <div id="easee">
            <div className="bg-white rounded-2xl p-5 border border-[#263A43]/12 h-full flex flex-col">
              <h3 className="font-bold text-gray-900 mb-1">Easee Home / Charge</h3>
              <p className="text-xs text-gray-500 mb-3">Easee · op til 22 kW · Type 2</p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4 flex-1">
                Easee er en kompakt, populær ladeboks. Vi sælger den ikke, men hvis du allerede har en Easee Home, Charge, Charge Lite eller Peak,
                kan du tilslutte den til enkel Saving Mode og få laderabat på 10 øre/kWh – uden at købe ny hardware.
              </p>
              <div className="mt-2">
                <ProsCons
                  pros={['Kompakt, diskret design', 'Virker med enkel Saving Mode', 'Type 2 – passer alle elbiler']}
                  cons={['Sælges ikke hos enkel', 'Ikke V2G-ready', 'Ekstra service-abonnement hos andre udbydere']}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProseSection id="anbefaling" heading="Vores anbefaling" alt>
        <FactGrid columns={3} facts={[
          { value: 'NexBlue Edge 2', label: 'Bedst til prisen', sub: `${ENKEL_PRODUCTS.chargers.nexblue_edge_2.price.toLocaleString('da-DK')} kr. · V2G-certificeret` },
          { value: 'Zaptec Go', label: 'Mest pålidelig', sub: `${ENKEL_PRODUCTS.chargers.zaptec_go.price.toLocaleString('da-DK')} kr. · gennemtestet` },
          { value: 'Har du Easee?', label: 'Behold den', sub: 'Tilslut til Saving Mode gratis' },
        ]} />
        <p className="mt-6">
          Skal du købe ny ladeboks, anbefaler vi NexBlue Edge 2 eller Zaptec gennem enkel energi – begge inkluderer Saving Mode, serviceaftale
          og laderabat til 0 kr./md. Har du allerede en Easee, er der ingen grund til at skifte hardware: tilslut den til enkel-appen og få samme
          fordele. Uanset valg betaler du spotpris uden tillæg fra 18 kr./md. uden binding.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/zaptec-go', label: 'Zaptec Go – produktside' },
        { path: '/zaptec-go-2', label: 'Zaptec Go 2 – produktside' },
        { path: '/nexblue-edge-2', label: 'NexBlue Edge 2 – produktside' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks skal jeg vælge?' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA
        headline="Find den rigtige ladeboks"
        subtext="Zaptec og NexBlue inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md. Har du allerede en Easee, virker det også."
        primary={{ path: '/opladere', text: 'Se ladebokse' }}
        secondary={{ path: '/hvilken-ladeboks-skal-jeg-vaelge', text: 'Køberguide' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}